/**
 * App-wide facade for client error reporting from UI. Boundaries and providers
 * should import from here, not from `sentryClient` / `datadogClient`, so the
 * presentation layer stays decoupled from the active monitoring implementation.
 */
import { isUnactionableClientNoise } from '../utils/isUnactionableClientNoise';
import { captureErrorToDatadog } from './datadogClient';
import { captureErrorToSentry } from './sentryClient';
import type { ErrorBoundaryReportContext } from './boundaryErrorContext';

export type { ErrorBoundaryReportContext } from './boundaryErrorContext';

/**
 * Send a React boundary (or app-level) error to the client monitoring pipeline.
 * Dual-ships to Sentry (when DSN set) and Datadog RUM (when DD app id/token set).
 * Expected cancellation, HMR, stale chunks, and resource-load Events are dropped.
 */
export function reportErrorFromBoundary(error: Error, context: ErrorBoundaryReportContext): void {
  if (isUnactionableClientNoise(error)) {
    return;
  }
  captureErrorToSentry(error, context);
  captureErrorToDatadog(error, context);
}
